import React, { createContext, useState, useContext, useEffect } from "react";

// Narration playback speeds — the single option set for BOTH the play
// bar's dropdown and the Preferences panel (2026-08-12 review #4: the
// two controls previously offered different sets, so a value picked in
// one looked unset in the other).
export const AUDIO_SPEEDS = [0.75, 1.0, 1.25, 1.5, 2.0];

// Stored values from older builds (0.8 / 1.2) snap to the closest
// current option instead of leaving both controls unhighlighted.
const snapToAudioSpeed = (value) =>
  AUDIO_SPEEDS.reduce((best, s) =>
    Math.abs(s - value) < Math.abs(best - value) ? s : best,
  );

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("preferredLanguage") || null;
  });

  const [ageCategory, setAgeCategory] = useState(() => {
    return localStorage.getItem("ageCategory") || "adult"; // 'children', 'adult', 'elder'
  });

  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem("fontSize");
    if (saved) return saved;
    // Default based on age category
    const age = localStorage.getItem("ageCategory") || "adult";
    return age === "elder" ? "large" : "medium";
  });

  const [audioSpeed, setAudioSpeed] = useState(() => {
    const saved = parseFloat(localStorage.getItem("audioSpeed"));
    if (!Number.isNaN(saved)) return snapToAudioSpeed(saved);
    // Default based on age category
    const age = localStorage.getItem("ageCategory") || "adult";
    return age === "elder" ? 0.75 : 1.0;
  });

  const [accessMethod, setAccessMethod] = useState(() => {
    return localStorage.getItem("accessMethod") || "qr"; // 'code' or 'qr'
  });

  useEffect(() => {
    localStorage.setItem("preferredLanguage", language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem("ageCategory", ageCategory);
  }, [ageCategory]);

  useEffect(() => {
    localStorage.setItem("fontSize", fontSize);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem("audioSpeed", audioSpeed.toString());
  }, [audioSpeed]);

  useEffect(() => {
    localStorage.setItem("accessMethod", accessMethod);
  }, [accessMethod]);

  const t = (translations) => {
    return translations[language] || translations["en"] || "";
  };

  // Update defaults when age category changes
  const updateAgeCategory = (newAge) => {
    setAgeCategory(newAge);
    // Update defaults based on age
    if (newAge === "elder") {
      setFontSize("large");
      setAudioSpeed(0.8);
    } else {
      setFontSize("medium");
      setAudioSpeed(1.0);
    }
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        ageCategory,
        setAgeCategory: updateAgeCategory,
        fontSize,
        setFontSize,
        audioSpeed,
        setAudioSpeed,
        accessMethod,
        setAccessMethod,
        t,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
